import {
  containsFrameStyle,
  documentOutputRatio,
  nativeSize,
  outputContainLayout,
  type Document,
  type SourceId,
} from '@/core/document'
import type { SourceImage } from '@/core/image'
import { PersistenceUnavailableError, PersistenceWriteError } from '@/core/persistence/errors'
import { THUMBNAIL_MAX_SIZE } from '@/core/persistence/types'
import { renderNode } from '@/core/render/renderNode'
import { ensureFrameFontsLoaded } from '@/tools/frame/fonts'
import { FrameStyleId } from '@/tools/frame/params'

const THUMBNAIL_MIME_TYPE = 'image/jpeg'
const THUMBNAIL_QUALITY = 0.8

export async function createThumbnail(
  document: Document,
  sources: ReadonlyMap<SourceId, SourceImage>,
): Promise<Blob> {
  if (typeof createImageBitmap !== 'function' || typeof OffscreenCanvas === 'undefined') {
    throw new PersistenceUnavailableError('This browser cannot create recent-edit thumbnails.')
  }
  if (sources.size === 0) throw new Error('Recent-edit thumbnail requires at least one source image')
  try {
    if (containsFrameStyle(document.root, FrameStyleId.Polaroid)) await ensureFrameFontsLoaded()
    const rootSize = nativeSize(document, sources, document.root.id)
    const layout = outputContainLayout(rootSize, documentOutputRatio(document, sources))
    const scale = Math.min(1, THUMBNAIL_MAX_SIZE / Math.max(layout.size.width, layout.size.height))
    const width = Math.max(1, Math.round(layout.size.width * scale))
    const height = Math.max(1, Math.round(layout.size.height * scale))
    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext('2d')
    if (!context) throw new PersistenceWriteError(new Error('Thumbnail canvas has no 2D context'))
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.fillStyle = document.output.background
    context.fillRect(0, 0, width, height)
    context.save()
    context.scale(width / layout.size.width, height / layout.size.height)
    context.translate(layout.rootRect.x, layout.rootRect.y)
    const previewSources = new Map([...sources].map(([id, source]) => [id, {
      ...source,
      bitmap: source.previewBitmap,
    }]))
    renderNode(document.root, document, previewSources, rootSize, context)
    context.restore()
    const thumbnail = await canvas.convertToBlob({ type: THUMBNAIL_MIME_TYPE, quality: THUMBNAIL_QUALITY })
    if (thumbnail.size === 0) throw new PersistenceWriteError(new Error('Thumbnail encoder produced an empty Blob'))
    return thumbnail
  } catch (error) {
    if (error instanceof PersistenceWriteError) throw error
    throw new PersistenceWriteError(error)
  }
}
